// Shared truck-speed pill.
// Prefers device-reported speed (phone Geolocation coords.speed, m/s) when the
// driver app sends it; otherwise derives speed from the change in a truck's GPS
// position between map refreshes. Renders a small pill just above the truck.
// Static truck (moved < 8 m since last fix) → "Stopped". Returns the pill marker
// (also pushed into markersOut if provided) or undefined when nothing to draw.
//
// prevMap: a useRef(new Map()) held by the calling component, keyed by truck id,
// storing { lat, lng, t } of the previous fix so speed survives across refreshes.
export function renderSpeedPill({ maps, map, prevMap, key, pos, deviceSpeed = null, markersOut, zIndex = 1200 }) {
  if (!maps || !map || !pos) return;
  const cur = { lat: parseFloat(pos.lat), lng: parseFloat(pos.lng) };
  if (isNaN(cur.lat) || isNaN(cur.lng)) return;

  const hav = (a, b) => {
    const toRad = (x) => (x * Math.PI) / 180;
    const R = 6371000;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(s));
  };

  const now = Date.now();
  let text = null; // null on the first-ever fix (unknown) → no pill yet

  // Preferred path: device-reported speed (m/s from phone Geolocation coords.speed).
  // True instantaneous speed — no guessing. -1/NaN means "no fix" → fall through to delta.
  const ds = deviceSpeed != null ? Number(deviceSpeed) : NaN;
  if (!isNaN(ds) && ds >= 0) {
    const mph = ds * 2.23694;
    text = mph >= 2 ? `${Math.round(mph)} mph` : 'Stopped';
    prevMap.set(key, { lat: cur.lat, lng: cur.lng, t: now });
  } else {
    // Fallback: derive speed from position delta between refreshes.
    const prev = prevMap.get(key);
    if (prev) {
      const moved = hav(prev, cur);
      if (moved < 8) {
        text = 'Stopped';
        // Keep the anchor position but advance the clock, so when the truck DOES
        // move, mph is measured over the latest interval — not from an ancient fix
        // (which made dt huge and pinned every truck to "Stopped").
        prevMap.set(key, { lat: prev.lat, lng: prev.lng, t: now });
      } else {
        const dtSec = Math.max((now - prev.t) / 1000, 1);
        const mph = (moved / dtSec) * 2.23694;
        text = mph >= 2 ? `${Math.round(mph)} mph` : 'Stopped';
        prevMap.set(key, { lat: cur.lat, lng: cur.lng, t: now });
      }
    } else {
      prevMap.set(key, { lat: cur.lat, lng: cur.lng, t: now });
    }
  }
  if (!text) return;

  const moving = text !== 'Stopped';
  const color = moving ? '#16a34a' : '#6b7280';
  const w = Math.max(54, text.length * 8 + 18);
  const svg = `
    <svg width="${w}" height="72" viewBox="0 0 ${w} 72" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="2" rx="9" ry="9" width="${w - 4}" height="20" fill="${color}" stroke="#ffffff" stroke-width="1.5"/>
      <text x="${w / 2}" y="16" text-anchor="middle" fill="#ffffff" font-family="Arial,sans-serif" font-size="11" font-weight="700">${text}</text>
    </svg>`;
  const marker = new maps.Marker({
    position: cur,
    map,
    icon: {
      url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
      scaledSize: new maps.Size(w, 72),
      anchor: new maps.Point(w / 2, 72), // bottom point → pill floats above the truck
    },
    zIndex,
    clickable: false,
  });
  if (markersOut && markersOut.push) markersOut.push(marker);
  return marker;
}
